import { type ReactNode, useState, useCallback, type FC } from 'react'
import { type SwipeEventData } from 'react-swipeable'

import { Swipeable } from './Swipeable.js'

type Props = {
  step: number
  threshold: number
  children?: ReactNode
  disableSwipe?: boolean
  preventScrollOnSwipe?: boolean
  onClose?: () => void
  onMove?: () => void
  onOpen?: () => void
}

export const MoveDown: FC<Props> = ({
  step,
  threshold,
  children,
  disableSwipe,
  preventScrollOnSwipe = true,
  onClose,
  onMove,
  onOpen,
}) => {
  const [translate, setTranslate] = useState(0)
  const [shouldClose, setShouldClose] = useState(false)

  const close = () => onClose?.()

  const open = () => {
    setTranslate(0)
    onOpen?.()
  }

  const setOverscrollBehaviour = (behaviour: string) => {
    const root = document.getElementById('root')
    if (root && root.style.overscrollBehaviorY !== behaviour) {
      root.style.overscrollBehaviorY = behaviour
    }
  }

  const stop = (data: SwipeEventData) => {
    setOverscrollBehaviour('contain')

    if (!data.initial[0] || !data.initial[1]) {
      return
    }

    if (shouldClose) {
      close()
    } else {
      open()
    }
  }

  const update = useCallback(
    (data: SwipeEventData) => {
      setOverscrollBehaviour('none')

      if (disableSwipe || !data.initial[0] || !data.initial[1]) {
        return
      }

      if (data.deltaY > 0) {
        if (Math.abs(data.deltaY - translate) > step) {
          onMove?.()
          setTranslate(data.deltaY)
          setShouldClose(data.deltaY > threshold && data.deltaY >= translate)
        }
      } else {
        setTranslate(0)
      }
    },
    [disableSwipe, onMove, step, threshold, translate]
  )

  const style = {
    transform: `translateY(${translate}px)`,
    transition: `transform 0.2s ease-out`,
  }

  return (
    <Swipeable
      trackMouse
      trackTouch
      delta={step}
      preventScrollOnSwipe={preventScrollOnSwipe}
      rotationAngle={0}
      onSwipedDown={stop}
      onSwiping={update}
    >
      <div style={style}>{children}</div>
    </Swipeable>
  )
}
